import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { 
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface GuidedTourProps {
  className?: string;
}

interface TooltipPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function GuidedTour({ className = '' }: GuidedTourProps) {
  const { currentStep, nextStep, previousStep, endOnboarding, isOnboarding } = useOnboarding();
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position based on target element
  const calculatePosition = (target: string, placement: string = 'bottom') => {
    const element = document.querySelector(target);
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
      width: rect.width,
      height: rect.height
    };
  };

  // Get tooltip positioning based on placement
  const getTooltipStyle = (elementPos: TooltipPosition, placement: string = 'bottom') => {
    const tooltipElement = tooltipRef.current;
    if (!tooltipElement) return {};

    const tooltipRect = tooltipElement.getBoundingClientRect();
    const margin = 12;

    switch (placement) {
      case 'top':
        return {
          top: elementPos.top - tooltipRect.height - margin,
          left: elementPos.left + (elementPos.width / 2) - (tooltipRect.width / 2),
        };
      case 'bottom':
        return {
          top: elementPos.top + elementPos.height + margin,
          left: elementPos.left + (elementPos.width / 2) - (tooltipRect.width / 2),
        };
      case 'left':
        return {
          top: elementPos.top + (elementPos.height / 2) - (tooltipRect.height / 2),
          left: elementPos.left - tooltipRect.width - margin,
        };
      case 'right':
        return {
          top: elementPos.top + (elementPos.height / 2) - (tooltipRect.height / 2),
          left: elementPos.left + elementPos.width + margin,
        };
      case 'center':
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        };
      default:
        return {
          top: elementPos.top + elementPos.height + margin,
          left: elementPos.left + (elementPos.width / 2) - (tooltipRect.width / 2),
        };
    }
  };

  // Update position when step changes
  useEffect(() => {
    if (!isOnboarding || !currentStep || !currentStep.target) {
      setIsVisible(false);
      setTooltipPosition(null);
      return;
    }

    const updatePosition = () => {
      const position = calculatePosition(currentStep.target!);
      if (position) {
        setTooltipPosition(position);
        setIsVisible(true);
        
        // Scroll element into view
        const element = document.querySelector(currentStep.target!);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      } else {
        setIsVisible(false);
      }
    };

    // Initial position calculation
    setTimeout(updatePosition, 100);

    // Update on window resize/scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [currentStep, isOnboarding]);

  // Don't render if not in guided tour mode
  if (!isOnboarding || !currentStep || !currentStep.target || !isVisible || !tooltipPosition) {
    return null;
  }

  const tooltipStyle = getTooltipStyle(tooltipPosition, currentStep.placement);

  return createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      {/* Overlay with spotlight effect */}
      <div 
        ref={overlayRef}
        className="absolute inset-0 pointer-events-auto"
        style={{
          background: `radial-gradient(circle at ${tooltipPosition.left + tooltipPosition.width / 2}px ${tooltipPosition.top + tooltipPosition.height / 2}px, transparent 0px, transparent ${Math.max(tooltipPosition.width, tooltipPosition.height) / 2 + 20}px, rgba(0, 0, 0, 0.4) ${Math.max(tooltipPosition.width, tooltipPosition.height) / 2 + 40}px)`
        }}
        onClick={() => {
          if (currentStep.skipable !== false) {
            endOnboarding(false);
          }
        }}
      />

      {/* Highlighted element border */}
      <div
        className="absolute border-4 border-indigo-500 rounded-lg shadow-lg pointer-events-none animate-pulse"
        style={{
          top: tooltipPosition.top - 4,
          left: tooltipPosition.left - 4,
          width: tooltipPosition.width + 8,
          height: tooltipPosition.height + 8,
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 p-4 max-w-sm pointer-events-auto"
        style={tooltipStyle}
      >
        {/* Arrow indicator */}
        {currentStep.placement !== 'center' && (
          <div
            className={`absolute w-3 h-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 transform rotate-45 ${
              currentStep.placement === 'top' 
                ? 'bottom-[-6px] left-1/2 -translate-x-1/2 border-b border-r' 
                : currentStep.placement === 'bottom'
                ? 'top-[-6px] left-1/2 -translate-x-1/2 border-t border-l'
                : currentStep.placement === 'left'
                ? 'right-[-6px] top-1/2 -translate-y-1/2 border-t border-r'
                : currentStep.placement === 'right'
                ? 'left-[-6px] top-1/2 -translate-y-1/2 border-b border-l'
                : ''
            }`}
          />
        )}

        {/* Close button */}
        {currentStep.skipable !== false && (
          <button
            onClick={() => endOnboarding(false)}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}

        {/* Content */}
        <div className="pr-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                {currentStep.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {currentStep.description}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={previousStep}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back
            </button>

            <div className="flex items-center space-x-2">
              {currentStep.skipable && (
                <button
                  onClick={() => endOnboarding(false)}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  Skip Tour
                </button>
              )}
              
              <button
                onClick={nextStep}
                className="flex items-center px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
              >
                Next
                <ArrowRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}